import React, { useEffect, useState, useMemo, useRef } from "react";
import { usePermissions } from "../hooks/usePermissions";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Switch } from "../components/ui/switch";
import { Plus, Wifi, WifiOff, Activity, Cpu, Server, AlertCircle, Trash2, Settings, Plug, Search, ChevronRight, ChevronLeft, Check, X, PartyPopper, Copy, ScrollText, ChevronDown, FlaskConical, Archive, RotateCcw, ArrowLeftRight, Upload, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useSiteContext } from "../components/layout/Layout";
import CsvImportDialog from "../components/CsvImportDialog";

// Devices connect to our EMQX broker over TLS on 8883 (8884 is the legacy plain port).
const DEFAULT_MQTT_BROKER = "mqtts://fireacms.com:8883";
const GATEWAY_CSV_TEMPLATE = [
  "gateway_id,gateway_name,site_id,hardware_brand,hardware_model,primary_protocol,mqtt_broker_url,mqtt_topic_prefix,ip_address,port,poll_interval_seconds,firmware_version",
  "868221079916055,Phoenix Marketcity Mall Gateway,site_67c77a1a,custom_mqtt,WT-460M,mqtt,mqtts://fireacms.com:8883,fire360pro/custom/868221079916055/data,,,10,SW:1.0.01",
];

const STATUS_TONE = {
  connected: { bg: "bg-emerald-500", text: "text-emerald-700", ring: "status-pulse-green", label: "CONNECTED" },
  error: { bg: "bg-red-500", text: "text-red-700", ring: "status-pulse-red", label: "ERROR" },
  disconnected: { bg: "bg-amber-500", text: "text-amber-700", ring: "", label: "DISCONNECTED" },
  unknown: { bg: "bg-slate-500", text: "text-slate-700", ring: "", label: "UNKNOWN" },
};

const PROTOCOL_LABELS = {
  bacnet_ip: "BACnet IP", bacnet_mstp: "BACnet MS/TP",
  modbus_tcp: "Modbus TCP", modbus_rtu: "Modbus RTU",
  mqtt: "MQTT", rest_api: "REST API", custom: "Custom",
};


const OUTPUT_SIGNAL_TYPES = ["digital_output", "binary_output", "analog_output"];

const WRITE_FUNCTION_OPTIONS = [
  { value: "15", label: "FC15 — Write Coils (relay boards)" },
  { value: "5", label: "FC05 — Write Single Coil" },
  { value: "6", label: "FC06 — Write Single Register" },
  { value: "16", label: "FC16 — Write Registers" },
];

const MODBUS_TRANSPORTS = [
  { value: "0", label: "RS-485" },
  { value: "1", label: "TCP/IP" },
  { value: "2", label: "LoRa" },
];

// Mirror of ModbusWriteSpec.csv_params() in backend/modbus_command.py, limited to the
// parameter string so the form can preview an ON command as it's typed. The backend remains
// the authority on what actually gets sent — including the raw frame and CRC, which are
// deliberately not duplicated here.
function previewWriteCommand(f) {
  const fc = Number(f.modbus_function_code ?? 15);
  const isCoil = fc === 5 || fc === 15;
  const params = [
    Number(f.modbus_transport ?? 0),
    f.modbus_tcp_ip || "0.0.0.0",
    Number(f.modbus_tcp_port || 502),
    f.modbus_slave_id,
    fc,
    f.modbus_start_address,
    1,
    isCoil ? "01" : "0001",
  ].join(",");
  return `$IPCFG,<DEVCMD: MBUSWR=${params} >`;
}

/* Direct Modbus addressing for a writable point.
   "Tag" writes only reach registers the gateway has pre-configured (its own built-in I/O);
   "direct" carries the full addressing in the payload and so reaches any slave on the bus —
   which is the only way to drive an external relay board. */
function WriteAddressingFields({ form, setForm, fieldBg }) {
  const isDirect = (form.write_mode || "tag") === "direct";
  const set = (patch) => setForm({ ...form, ...patch });
  const complete = form.modbus_slave_id && form.modbus_start_address;

  return (
    <div className="space-y-2">
      <Select value={form.write_mode || "tag"} onValueChange={(v) => set({ write_mode: v })}>
        <SelectTrigger className={`${fieldBg} border-gray-200`} data-testid="write-mode-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white border-gray-200 text-gray-900">
          <SelectItem value="tag">Write by gateway tag name (gateway's own I/O)</SelectItem>
          <SelectItem value="direct">Write by direct Modbus address (any slave on the bus)</SelectItem>
        </SelectContent>
      </Select>

      {isDirect && (
        <>
          <div className="text-[11px] text-gray-500">
            Addresses the slave directly, so no gateway-side tag is needed. Start address is
            <span className="font-semibold"> 1-based</span> — relay 1 is address 1 (on the wire, 0).
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number" min="1" max="247" placeholder="Slave ID (e.g. 1)"
              value={form.modbus_slave_id ?? ""}
              onChange={(e) => set({ modbus_slave_id: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid="modbus-slave-id"
            />
            <Input
              type="number" min="1" placeholder="Relay / coil number (1-based)"
              value={form.modbus_start_address ?? ""}
              onChange={(e) => set({ modbus_start_address: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid="modbus-start-address"
            />
            <Select
              value={String(form.modbus_function_code ?? 15)}
              onValueChange={(v) => set({ modbus_function_code: parseInt(v, 10) })}
            >
              <SelectTrigger className={`${fieldBg} border-gray-200`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                {WRITE_FUNCTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={String(form.modbus_transport ?? 0)}
              onValueChange={(v) => set({ modbus_transport: parseInt(v, 10) })}
            >
              <SelectTrigger className={`${fieldBg} border-gray-200`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                {MODBUS_TRANSPORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {Number(form.modbus_transport ?? 0) === 1 && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Slave IP" value={form.modbus_tcp_ip ?? ""} onChange={(e) => set({ modbus_tcp_ip: e.target.value })} className={`${fieldBg} border-gray-200 font-mono text-xs`} />
              <Input type="number" placeholder="Port (502)" value={form.modbus_tcp_port ?? ""} onChange={(e) => set({ modbus_tcp_port: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className={`${fieldBg} border-gray-200 font-mono text-xs`} />
            </div>
          )}

          {complete && (
            <div className="rounded-lg bg-gray-900 text-emerald-300 px-3 py-2 font-mono text-[10px] break-all" data-testid="write-command-preview">
              <div className="text-gray-500 uppercase tracking-wider text-[9px] mb-1">Turn-ON command preview</div>
              {previewWriteCommand(form)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function fmtAgo(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

// Parses a form field into a number for the API, or undefined if it should be omitted.
// Deliberately never returns NaN: JSON.stringify silently turns NaN into `null` (JSON has no
// NaN), and the backend's IOMapping model declares these fields as plain `float`, not
// `Optional[float]` — an explicit `null` fails validation even though a default exists,
// producing a 422 ("Input should be a valid number") that gives no hint the real problem was an
// unset/empty field on our end. Found 2026-08-24: happened on every fresh "Add Mapping" for a
// binary/digital point, because the drawer's initial mapForm reset left engineering_min/max as
// `undefined` (key missing) rather than `""`, and parseFloat(undefined) is NaN.
function numOrUndefined(v) {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
}

// "5" + "860710086582429" -> "860710086582429_s5" — device_id must carry the slave suffix for
// per-slave confirmation scoping to work (see gateway_commands.py's _confirm_via_readback and
// GatewayCommands.jsx's currentReading, fixed 2026-08-24); a bare gateway_id falls back to the
// old ungated behavior, which is wrong whenever two slaves report a same-named register.
function deviceIdForSlave(gatewayId, slaveId) {
  const s = (slaveId || "").trim();
  return s ? `${gatewayId}_s${s}` : gatewayId;
}

// Inverse of deviceIdForSlave — for pre-filling the Slave ID field when editing a mapping that
// already has a slave-qualified device_id.
function slaveIdFromDeviceId(gatewayId, deviceId) {
  if (!deviceId || !gatewayId) return "";
  const prefix = `${gatewayId}_s`;
  return deviceId.startsWith(prefix) ? deviceId.slice(prefix.length) : "";
}

// ── Live registers ──────────────────────────────────────────────────────────────────────────
// The IO Mappings drawer offers the gateway's live registers (same API as Gateway Commands'
// Live Register Monitor) for Slave ID and Register Address instead of typing them blind.
const PICK_NONE = "__none";
const PICK_MANUAL = "__manual";

// One row per (slave, register) for this gateway, latest value only.
function liveRegistersForGateway(rows, gatewayId) {
  const byKey = new Map();
  for (const r of rows || []) {
    if (r.gateway_id !== gatewayId || !r.parameter) continue;
    const slave = r.slave_id != null ? String(r.slave_id) : slaveIdFromDeviceId(gatewayId, r.device_id);
    const key = `${slave}::${r.parameter}`;
    const prev = byKey.get(key);
    if (!prev || (r.ingested_at || "") > (prev.ingested_at || "")) {
      byKey.set(key, { slave, parameter: r.parameter, value: r.value, unit: r.unit || "", ingested_at: r.ingested_at });
    }
  }
  return [...byKey.values()].sort((a, b) => (Number(a.slave) - Number(b.slave)) || a.parameter.localeCompare(b.parameter));
}

// WT-460M-style names: …di-1 / di1 → digital input, out… / output… / relay → output, pulse… →
// pulse counter, anything else (ch123, adi1, sysvolt) → analog input. Only a starting point.
function guessSignalType(name) {
  const n = (name || "").toLowerCase();
  if (/pulse/.test(n)) return "pulse_counter";
  if (/^out|output|relay|(^|[-_])do\d*([-_]|$)/.test(n)) return "digital_output";
  if (/^di\d|(^|[-_])di([-_\d]|$)|input/.test(n)) return "digital_input";
  return "analog_input";
}

const LIVE_TYPE_LABEL = { analog_input: "Analog in", digital_input: "Digital in", pulse_counter: "Pulse", digital_output: "Output" };
const fmtLiveValue = (v) => (v === null || v === undefined ? "—" : Number.isInteger(v) ? String(v) : Number(v).toFixed(2));

// Dropdown of known Slave IDs, with "Enter manually" for one that isn't reporting yet.
function SlavePicker({ value, onChange, options, fieldBg, allowNone = true, testid }) {
  const [manualPick, setManualPick] = useState(false);
  const known = options.some((o) => o.id === value);
  if (manualPick || (value && !known)) {
    return (
      <div className="flex gap-1">
        <Input autoFocus={manualPick} placeholder="Slave ID (e.g. 50)" value={value || ""} onChange={(e) => onChange(e.target.value.trim())}
          className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid={testid} />
        {options.length > 0 && <Button type="button" variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={() => { setManualPick(false); onChange(""); }}>List</Button>}
      </div>
    );
  }
  return (
    <Select value={value || (allowNone ? PICK_NONE : "")} onValueChange={(v) => (v === PICK_MANUAL ? setManualPick(true) : onChange(v === PICK_NONE ? "" : v))}>
      <SelectTrigger className={`${fieldBg} border-gray-200 text-xs`} data-testid={testid}><SelectValue placeholder="Slave ID" /></SelectTrigger>
      <SelectContent className="bg-white border-gray-200 text-gray-900">
        {allowNone && <SelectItem value={PICK_NONE}>No slave (whole gateway)</SelectItem>}
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        <SelectItem value={PICK_MANUAL}>Enter manually…</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Dropdown of the slave's live registers (name — current value), or typed by hand.
function RegisterPicker({ value, onPick, onManual, registers, fieldBg, placeholder, maxLength, testid }) {
  const [manualPick, setManualPick] = useState(false);
  const known = registers.some((r) => r.parameter === value);
  if (manualPick || (value && !known) || !registers.length) {
    return (
      <div className="flex gap-1">
        <Input autoFocus={manualPick} placeholder={placeholder} maxLength={maxLength} value={value || ""} onChange={(e) => onManual(e.target.value)}
          className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid={testid} />
        {registers.length > 0 && <Button type="button" variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={() => { setManualPick(false); onManual(""); }}>List</Button>}
      </div>
    );
  }
  return (
    <Select value={value || ""} onValueChange={(v) => (v === PICK_MANUAL ? setManualPick(true) : onPick(registers.find((r) => r.parameter === v)))}>
      <SelectTrigger className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
        {registers.map((r) => (
          <SelectItem key={`${r.slave}::${r.parameter}`} value={r.parameter}>
            <span className="font-mono">{r.parameter}</span>
            <span className="text-gray-400"> — {fmtLiveValue(r.value)}{r.unit ? ` ${r.unit}` : ""}{r.slave ? ` · slave ${r.slave}` : ""}</span>
          </SelectItem>
        ))}
        <SelectItem value={PICK_MANUAL}>Enter manually…</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Slave + register fields shared by Add Mapping and Edit. For an input, the live register IS the
// address. For an output it is NOT: on the WT-460M an output reports back under a different name
// than the tag it's written by (writes OUT1, reads back output-1), and writing to the readback
// name is rejected — so outputs keep a typed write tag and the live register fills Readback.
function LiveRegisterFields({ form, setForm, live, slaveOptions, fieldBg }) {
  const output = OUTPUT_SIGNAL_TYPES.includes(form.signal_type);
  const regs = form.slave_id ? live.filter((r) => r.slave === form.slave_id) : live;
  const pick = (row) => {
    if (!row) return;
    const guess = guessSignalType(row.parameter);
    const next = {
      ...form,
      slave_id: row.slave || form.slave_id,
      io_point_name: form.io_point_name || row.parameter,
      engineering_unit: form.engineering_unit || row.unit || "",
    };
    if (output || OUTPUT_SIGNAL_TYPES.includes(guess)) {
      next.signal_type = output ? form.signal_type : guess;
      next.readback_io_point_name = row.parameter;
    } else {
      next.signal_type = guess;
      next.io_point_address = row.parameter;
    }
    setForm(next);
  };
  const direct = form.write_mode === "direct";
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <div className="text-[11px] font-semibold text-gray-600 mb-1">Slave ID</div>
        <SlavePicker value={form.slave_id || ""} options={slaveOptions} fieldBg={fieldBg}
          onChange={(v) => setForm({ ...form, slave_id: v })} testid="map-slave" />
      </div>
      {!output ? (
        <div>
          <div className="text-[11px] font-semibold text-gray-600 mb-1">Register address</div>
          <RegisterPicker value={form.io_point_address || ""} registers={regs} fieldBg={fieldBg} placeholder="Choose a live register"
            onPick={pick} onManual={(v) => setForm({ ...form, io_point_address: v })} testid="map-register" />
        </div>
      ) : (
        <div>
          <div className="text-[11px] font-semibold text-gray-600 mb-1">{direct ? "Point key" : "Write tag"}</div>
          <Input placeholder={direct ? "e.g. RELAY-BOARD-1-K1" : "Device's write tag, e.g. OUT1"} maxLength={direct ? 32 : 13}
            value={form.io_point_address || ""} onChange={(e) => setForm({ ...form, io_point_address: e.target.value })}
            className={`${fieldBg} border-gray-200 font-mono text-xs`} data-testid="map-write-tag" />
        </div>
      )}
      {output && (
        <div className="col-span-2">
          <div className="text-[11px] font-semibold text-gray-600 mb-1">Reads back as (optional)</div>
          <RegisterPicker value={form.readback_io_point_name || ""} registers={regs} fieldBg={fieldBg} placeholder="Live register that reports this output"
            onPick={pick} onManual={(v) => setForm({ ...form, readback_io_point_name: v })} testid="map-readback" />
          <div className="text-[10px] text-gray-500 mt-1">Used to confirm a write took effect. Not the write tag — the device rejects writes to a readback name.</div>
        </div>
      )}
    </div>
  );
}

// Mirrors backend/device_adapters/generic_mqtt.py's _resolve_json_path — dot keys with
// optional trailing [N] indices, fully defensive (undefined instead of throwing).
function resolveJsonPath(payload, path) {
  let cur = payload;
  for (const rawPart of path.split(".")) {
    const part = rawPart.trim();
    if (!part) return undefined;
    const m = part.match(/^([^[\]]+)((?:\[\d+\])*)$/);
    if (!m) return undefined;
    const [, key, idxPart] = m;
    if (typeof cur !== "object" || cur === null || Array.isArray(cur) || !(key in cur)) return undefined;
    cur = cur[key];
    for (const idxMatch of idxPart.matchAll(/\[(\d+)\]/g)) {
      const i = Number(idxMatch[1]);
      if (!Array.isArray(cur) || i >= cur.length) return undefined;
      cur = cur[i];
    }
  }
  return cur;
}

function JsonPathFeedback({ parsedSample, path }) {
  if (!path) return null;
  if (!parsedSample) {
    return <div className="text-[11px] text-gray-500 mt-1">No sample payload to validate against yet.</div>;
  }
  const value = resolveJsonPath(parsedSample, path);
  if (value === undefined) {
    return <div className="text-[11px] text-red-700 mt-1">No match in sample payload.</div>;
  }
  const display = typeof value === "object" ? JSON.stringify(value) : String(value);
  return <div className="text-[11px] text-emerald-600 mt-1 font-mono">Matches: {display}</div>;
}

const EVENT_TONE = {
  connected: { dot: "bg-emerald-500", text: "text-emerald-700" },
  disconnected: { dot: "bg-amber-500", text: "text-amber-700" },
  parse_error: { dot: "bg-red-500", text: "text-red-700" },
};

function KpiCard({ label, value, icon: Icon, tone = "blue", testid, onClick }) {
  const grad = { blue: "from-sky-400 to-indigo-500", green: "from-emerald-400 to-emerald-600", red: "from-red-500 to-pink-600", orange: "from-orange-500 to-pink-500" };
  return (
    <div className={`glass p-5 rounded-lg ${onClick ? "cursor-pointer hover:bg-gray-50 transition" : ""}`} data-testid={testid} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad[tone]} flex items-center justify-center`}><Icon className="w-5 h-5 text-gray-900" /></div>
      </div>
      <div className="mt-4">
        <div className="text-3xl font-extrabold">{value}</div>
        <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function HardwareGateways() {
  // Add / edit / delete and gateway lifecycle actions are super-admin-only (backend enforces the
  // same). Viewing, Connection Log and Test stay available to everyone who can open this page.
  const { can } = usePermissions();
  const canManage = can("hardware_gateways", "update");
  const { sites, selectedSite, selectedOrg } = useSiteContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showOnboardBanner, setShowOnboardBanner] = useState(searchParams.get("onboarded") === "1");
  const [gateways, setGateways] = useState([]);
  const [stuckSummary, setStuckSummary] = useState({ count: 0, items: [] });
  const [stuckPopoverOpen, setStuckPopoverOpen] = useState(false);
  const [adapters, setAdapters] = useState({});
  const [filter, setFilter] = useState({ site: "all", brand: "all", protocol: "all", status: "all", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm());
  const [ioDrawer, setIoDrawer] = useState(null);
  const [ioRows, setIoRows] = useState([]);
  const [sample, setSample] = useState(null);
  const parsedSample = useMemo(() => {
    if (!sample?.last_raw_sample) return null;
    try { return JSON.parse(sample.last_raw_sample); } catch { return null; }
  }, [sample]);
  const [mapForm, setMapForm] = useState({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "", engineering_min: "", engineering_max: "", readback_io_point_name: "", write_mode: "tag" });
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [editMapForm, setEditMapForm] = useState({});
  const [slaveRows, setSlaveRows] = useState([]);
  const [slaveForm, setSlaveForm] = useState({ slave_id: "", label: "" });
  const [liveRegs, setLiveRegs] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [mappingAll, setMappingAll] = useState(false);
  const [liveSlaveFilter, setLiveSlaveFilter] = useState("all");
  const [logDrawer, setLogDrawer] = useState(null);
  const [logData, setLogData] = useState({ events: [], recent_messages: [], register_health: [] });
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [simDrawer, setSimDrawer] = useState(null);
  const [simPayloadText, setSimPayloadText] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simSending, setSimSending] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [replaceDrawer, setReplaceDrawer] = useState(null);
  const [newSerial, setNewSerial] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [credResult, setCredResult] = useState(null); // { gateway_id, mqtt_username, mqtt_password }
  const [showCredPassword, setShowCredPassword] = useState(false);
  const [knownPassword, setKnownPassword] = useState("");

  // Site starts empty on purpose: defaulting it to the header's site silently registered a
  // gateway meant for another site under the one that happened to be selected (2026-10-09).
  function initialForm() {
    return {
      gateway_id: "", gateway_name: "", site_id: "", hardware_brand: "easyio_fw", hardware_model: "FW-28",
      primary_protocol: "bacnet_ip", secondary_protocol: "", ip_address: "", port: 47808,
      bacnet_device_id: 1001, modbus_unit_id: 1, mqtt_broker_url: "", mqtt_topic_prefix: "",
      api_endpoint: "", poll_interval_seconds: 30, firmware_version: "", notes: "",
    };
  }

  const load = () => api.get("/hardware/gateways", { params: { include_inactive: showInactive } }).then((r) => setGateways(r.data));
  const loadStuckSummary = () => api.get("/hardware/gateways/stuck-registers-summary").then((r) => setStuckSummary(r.data)).catch(() => {});
  useEffect(() => { load(); loadStuckSummary(); api.get("/hardware/protocol-adapters").then((r) => setAdapters(r.data)); }, [showInactive]);

  const brandLabel = (key) => adapters[key]?.label || key;

  // Auto-fill defaults when brand changes
  useEffect(() => {
    if (!form.hardware_brand || !adapters[form.hardware_brand]) return;
    const a = adapters[form.hardware_brand];
    setForm((f) => ({ ...f, primary_protocol: a.primary_protocol || f.primary_protocol, port: a.default_port || f.port }));
  }, [form.hardware_brand, adapters]);

  // MQTT devices publish to our own EMQX broker on a topic the backend already subscribes to, so
  // both fields can be filled in from the brand and Gateway ID. Generic MQTT devices use
  // fire360pro/custom/{id}/data; WT-460M-style gateways use iot1/{IMEI}/. A field is only
  // overwritten while it still holds the last value filled in here, so manual edits stick.
  const lastAuto = useRef({ broker: "", topic: "" });
  useEffect(() => {
    if (form.primary_protocol !== "mqtt") return;
    const a = adapters[form.hardware_brand] || {};
    const broker = a.mqtt_broker ? `mqtts://${a.mqtt_broker}:${a.default_port || 8883}` : DEFAULT_MQTT_BROKER;
    const topic = a.topic_template ? "" // vendor cloud with its own topic scheme
      : !form.gateway_id ? ""
      : form.hardware_brand === "custom_mqtt" ? `fire360pro/custom/${form.gateway_id}/data`
      : `iot1/${form.gateway_id}/`;
    setForm((f) => {
      const next = { ...f };
      if (!f.mqtt_broker_url || f.mqtt_broker_url === lastAuto.current.broker) next.mqtt_broker_url = broker;
      // Generic MQTT's topic is fixed — the backend only subscribes to fire360pro/custom/+/data.
      const forced = f.hardware_brand === "custom_mqtt";
      if (topic && (forced || !f.mqtt_topic_prefix || f.mqtt_topic_prefix === lastAuto.current.topic)) next.mqtt_topic_prefix = topic;
      lastAuto.current = { broker, topic };
      return next.mqtt_broker_url === f.mqtt_broker_url && next.mqtt_topic_prefix === f.mqtt_topic_prefix ? f : next;
    });
  }, [form.primary_protocol, form.hardware_brand, form.gateway_id, adapters]);

  // Follow the site picked in the header; the local Site dropdown can still widen to "All sites".
  useEffect(() => { setFilter((f) => ({ ...f, site: selectedSite || "all" })); }, [selectedSite]);

  // /hardware/gateways returns every org's gateways to a super admin — keep to the header's org.
  const orgGateways = useMemo(
    () => (selectedOrg ? gateways.filter((g) => g.org_id === selectedOrg) : gateways),
    [gateways, selectedOrg],
  );

  const filtered = useMemo(() => orgGateways.filter((g) => {
    if (filter.site !== "all" && g.site_id !== filter.site) return false;
    if (filter.brand !== "all" && g.hardware_brand !== filter.brand) return false;
    if (filter.protocol !== "all" && g.primary_protocol !== filter.protocol) return false;
    if (filter.status !== "all" && g.connection_status !== filter.status) return false;
    if (filter.q && !`${g.gateway_name} ${g.ip_address || ""}`.toLowerCase().includes(filter.q.toLowerCase())) return false;
    return true;
  }), [orgGateways, filter]);

  const kpis = useMemo(() => {
    const total = orgGateways.length;
    const online = orgGateways.filter((g) => g.connection_status === "connected").length;
    const issues = orgGateways.filter((g) => g.connection_status === "error" || g.connection_status === "disconnected").length;
    const io = orgGateways.reduce((s, g) => s + (g.total_io_points || 0), 0);
    return { total, online, issues, io };
  }, [orgGateways]);

  const save = async () => {
    if (!form.gateway_name || !form.site_id) { toast.error("Name and site are required"); return; }
    if (!editing && !form.gateway_id) { toast.error("Serial Number / IMEI is required"); return; }
    try {
      if (editing) await api.patch(`/hardware/gateways/${editing.gateway_id}`, form);
      else await api.post("/hardware/gateways", form);
      toast.success(editing ? "Gateway updated" : "Gateway added");
      setOpen(false); setEditing(null); setForm(initialForm()); setStep(1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const siteLabel = (siteId) => sites.find((s) => s.site_id === siteId)?.name || siteId || "—";

  // Delete is permanent and also wipes the IO mappings and the device's MQTT login, so it gets a
  // real dialog: what is lost, a pointer to Decommission, and the IMEI (not the site name, which
  // every gateway on the site shares) typed back to confirm.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const remove = (g) => { setDeleteTarget(g); setDeleteTyped(""); };
  const confirmRemove = async () => {
    const g = deleteTarget;
    if (!g || deleteTyped.trim() !== g.gateway_id) return;
    setDeleting(true);
    try {
      await api.delete(`/hardware/gateways/${g.gateway_id}`);
      toast.success(`Deleted ${g.gateway_name}`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      load();
    } finally {
      setDeleting(false);
    }
  };

  const decommission = async (g) => {
    if (!window.confirm(`Decommission "${g.gateway_name}"? Its IO mappings and history are kept — you can reactivate it anytime.`)) return;
    await api.post(`/hardware/gateways/${g.gateway_id}/decommission`);
    toast.success("Gateway decommissioned"); load();
  };

  const reactivate = async (g) => {
    await api.post(`/hardware/gateways/${g.gateway_id}/reactivate`);
    toast.success("Gateway reactivated"); load();
  };

  const openReplace = (g) => { setReplaceDrawer(g); setNewSerial(""); };
  const sendReplace = async () => {
    if (!newSerial.trim()) { toast.error("Enter the new device's serial / IMEI"); return; }
    setReplacing(true);
    try {
      await api.post(`/hardware/gateways/${replaceDrawer.gateway_id}/replace`, { new_gateway_id: newSerial.trim() });
      toast.success("Hardware replaced — IO mappings carried over to the new device");
      setReplaceDrawer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(false);
    }
  };

  const test = async (g) => {
    toast.message("Testing connection…", {
      description: g.primary_protocol === "mqtt" ? "Checking MQTT broker for an active client" : "Attempting TCP connection — protocol handshake not verified",
    });
    try {
      const r = await api.post(`/hardware/gateways/${g.gateway_id}/test`);
      const caveat = r.data.check_type === "tcp_reachability" ? " (TCP reachability only)" : "";
      if (r.data.success) toast.success(r.data.message + caveat, { description: `${r.data.latency_ms} ms` });
      else toast.error(r.data.message || "Connection failed");
      load();
    } catch { toast.error("Test failed — backend unreachable"); }
  };

  const validateStep2 = () => {
    if (form.primary_protocol === "modbus_tcp" || form.primary_protocol === "modbus_rtu") {
      const id = Number(form.modbus_unit_id);
      if (!id || id < 1 || id > 247) { toast.error("Modbus Unit ID must be between 1 and 247"); return false; }
    }
    if (form.primary_protocol === "mqtt") {
      if (!form.mqtt_broker_url) { toast.error("MQTT Broker URL is required"); return false; }
      if (!form.mqtt_topic_prefix) { toast.error("MQTT Topic Prefix is required"); return false; }
    }
    return true;
  };

  const goNext = () => {
    if (step === 1 && !form.site_id) { toast.error("Select the site this gateway is installed at"); return; }
    if (step === 2 && !validateStep2()) return;
    setStep(step + 1);
  };

  const openEdit = (g) => { setEditing(g); setForm({ ...initialForm(), ...g }); setStep(1); setOpen(true); };
  const openIo = async (g) => {
    setIoDrawer(g);
    setLiveSlaveFilter("all");
    setMapForm({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "", engineering_min: "", engineering_max: "", readback_io_point_name: "", write_mode: "tag", slave_id: "" });
    setSlaveForm({ slave_id: "", label: "" });
    const r = await api.get(`/hardware/gateways/${g.gateway_id}/io-mappings`);
    setIoRows(r.data);
    const sl = await api.get(`/hardware/gateways/${g.gateway_id}/slaves`);
    setSlaveRows(sl.data);
    loadLive(g);
    if (g.hardware_brand === "custom_mqtt") {
      const s = await api.get(`/hardware/gateways/${g.gateway_id}/sample-payload`);
      setSample(s.data);
    } else {
      setSample(null);
    }
  };
  const deleteIo = async (m) => {
    await api.delete(`/hardware/io-mappings/${m.mapping_id}`);
    toast.success("IO mapping removed");
    const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
    setIoRows(r.data); load();
  };
  // Same source as Gateway Commands' Live Register Monitor, narrowed to this gateway.
  const loadLive = async (g = ioDrawer) => {
    if (!g?.site_id) { setLiveRegs([]); return; }
    setLiveLoading(true);
    try {
      const r = await api.get(`/v1/sites/${g.site_id}/telemetry/latest`, { params: { limit: 2000 } });
      setLiveRegs(liveRegistersForGateway(r.data, g.gateway_id));
    } catch { setLiveRegs([]); }
    finally { setLiveLoading(false); }
  };
  // Every slave seen in live data or already registered, for the Slave ID dropdowns.
  const slaveOptions = useMemo(() => {
    const ids = new Map();
    for (const r of liveRegs) if (r.slave) ids.set(r.slave, (ids.get(r.slave) || 0) + 1);
    const label = (id) => slaveRows.find((s) => String(s.slave_id) === id)?.label;
    const all = new Set([...ids.keys(), ...slaveRows.map((s) => String(s.slave_id))]);
    return [...all].sort((a, b) => Number(a) - Number(b)).map((id) => ({
      id,
      label: `Slave ${id}${label(id) ? ` — ${label(id)}` : ""} · ${ids.has(id) ? `live, ${ids.get(id)} registers` : "no live data"}`,
    }));
  }, [liveRegs, slaveRows]);
  // The mapping a live register already has, if any (same slave, or an unscoped mapping).
  const mappingFor = (reg) => ioRows.find((m) => {
    if (![m.io_point_address, m.io_point_name, m.readback_io_point_name].includes(reg.parameter)) return false;
    const s = slaveIdFromDeviceId(m.gateway_id, m.device_id);
    return !s || s === reg.slave;
  });
  const pickLiveRegister = (reg) => {
    const guess = guessSignalType(reg.parameter);
    const output = OUTPUT_SIGNAL_TYPES.includes(guess);
    setMapForm({
      io_point_name: reg.parameter, json_path: "", io_point_address: output ? "" : reg.parameter,
      signal_type: guess, engineering_unit: reg.unit || "", engineering_min: "", engineering_max: "",
      readback_io_point_name: output ? reg.parameter : "", write_mode: "tag", slave_id: reg.slave,
    });
    if (output) toast.message(`${reg.parameter} looks like an output — enter its write tag (e.g. OUT1) before adding`);
    document.querySelector('[data-testid="add-mapping-section"]')?.scrollIntoView({ behavior: "smooth" });
  };
  // Inputs only: an output's live name is its readback name, not its write tag (see LiveRegisterFields).
  const mapAllLive = async () => {
    const todo = liveRegs.filter((r) => !mappingFor(r) && !OUTPUT_SIGNAL_TYPES.includes(guessSignalType(r.parameter)));
    const skipped = liveRegs.filter((r) => !mappingFor(r) && OUTPUT_SIGNAL_TYPES.includes(guessSignalType(r.parameter)));
    if (!todo.length) { toast.message(skipped.length ? "Only outputs are left — add those one at a time with their write tag" : "Every live register is already mapped"); return; }
    if (!window.confirm(`Add ${todo.length} mapping${todo.length > 1 ? "s" : ""} from live data?${skipped.length ? `\n\n${skipped.length} output${skipped.length > 1 ? "s" : ""} (${skipped.map((r) => r.parameter).join(", ")}) will be skipped — add them one at a time with their write tag.` : ""}`)) return;
    setMappingAll(true);
    let ok = 0;
    for (const r of todo) {
      try {
        await api.post(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`, {
          gateway_id: ioDrawer.gateway_id, device_id: deviceIdForSlave(ioDrawer.gateway_id, r.slave),
          io_point_name: r.parameter, io_point_address: r.parameter,
          signal_type: guessSignalType(r.parameter), engineering_unit: r.unit || undefined, write_mode: "tag",
        });
        ok += 1;
      } catch { /* reported below */ }
    }
    setMappingAll(false);
    if (ok === todo.length) toast.success(`${ok} mapping${ok > 1 ? "s" : ""} added`);
    else toast.error(`${ok} of ${todo.length} mappings added — the rest failed`);
    const res = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
    setIoRows(res.data); load();
  };

  const startEditMapping = (m) => {
    setEditingMappingId(m.mapping_id);
    setEditMapForm({ ...m, slave_id: slaveIdFromDeviceId(m.gateway_id, m.device_id) });
  };
  const cancelEditMapping = () => {
    setEditingMappingId(null);
    setEditMapForm({});
  };
  const updateMapping = async (mappingId) => {
    try {
      // slave_id is transient form state (see slaveIdFromDeviceId), not a real IOMapping field —
      // fold it into device_id and strip it before sending.
      const { slave_id, ...rest } = editMapForm;
      const payload = {
        ...rest,
        engineering_min: numOrUndefined(editMapForm.engineering_min),
        engineering_max: numOrUndefined(editMapForm.engineering_max),
        device_id: deviceIdForSlave(editMapForm.gateway_id || ioDrawer.gateway_id, slave_id),
      };
      await api.patch(`/hardware/io-mappings/${mappingId}`, payload);
      toast.success("Mapping updated");
      setEditingMappingId(null);
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
      setIoRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update mapping");
    }
  };
  const addSlave = async () => {
    if (!slaveForm.slave_id) { toast.error("Slave ID is required"); return; }
    try {
      await api.post(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`, slaveForm);
      toast.success("Slave ID registered");
      setSlaveForm({ slave_id: "", label: "" });
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`);
      setSlaveRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register slave ID");
    }
  };
  const deleteSlave = async (slaveId) => {
    await api.delete(`/hardware/gateways/${ioDrawer.gateway_id}/slaves/${slaveId}`);
    toast.success("Slave ID removed");
    const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`);
    setSlaveRows(r.data);
  };
  const openLog = async (g) => {
    setLogDrawer(g);
    setExpandedMsg(null);
    const r = await api.get(`/hardware/gateways/${g.gateway_id}/connection-log`);
    setLogData(r.data);
  };

  const canSimulate = (g) => g.hardware_brand === "custom_mqtt" || (g.hardware_model || "").toUpperCase() === "WT-460M";
  const openSimulate = (g) => { setSimDrawer(g); setSimPayloadText(""); setSimResult(null); };

  const credBase = (g) => ({ gateway_id: g.gateway_id, gateway_name: g.gateway_name, mqtt_broker_url: g.mqtt_broker_url, mqtt_topic_prefix: g.mqtt_topic_prefix });
  const viewCredentials = async (g) => {
    try {
      const r = await api.get(`/hardware/gateways/${g.gateway_id}/mqtt-credentials`);
      setCredResult({ ...credBase(g), ...r.data });
      setShowCredPassword(false); setKnownPassword("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load credentials"); }
  };
  const rotateCredentials = async (g) => {
    if (!window.confirm(`Generate new MQTT password for ${g.gateway_name}?\nThe old password stops working immediately — update the device config straight away.`)) return;
    try {
      const r = await api.post(`/hardware/gateways/${g.gateway_id}/rotate-credentials`);
      setCredResult({ ...credBase(g), ...r.data, rotated: true });
      setShowCredPassword(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to rotate credentials"); }
  };
  const saveKnownPassword = async () => {
    const g = credResult;
    try {
      const r = await api.put(`/hardware/gateways/${g.gateway_id}/mqtt-credentials`, { password: knownPassword });
      setCredResult({ ...g, ...r.data }); setKnownPassword(""); toast.success("Password saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save password"); }
  };
  const sendSimulation = async () => {
    setSimSending(true);
    setSimResult(null);
    try {
      let payload;
      if (simPayloadText.trim()) {
        try { payload = JSON.parse(simPayloadText); }
        catch { toast.error("Payload must be valid JSON"); setSimSending(false); return; }
      }
      const r = await api.post(`/hardware/gateways/${simDrawer.gateway_id}/simulate`, payload ? { payload } : {});
      setSimResult(r.data);
      if (r.data.success) toast.success("Test payload ingested successfully");
      else toast.error(r.data.message || r.data.error || "Simulation did not produce data");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation failed");
    } finally {
      setSimSending(false);
    }
  };

  const addMapping = async () => {
    const isCustomMqtt = ioDrawer.hardware_brand === "custom_mqtt";
    const addressField = isCustomMqtt ? mapForm.json_path : mapForm.io_point_address;
    if (!mapForm.io_point_name || !addressField) {
      toast.error(`IO Point Name and ${isCustomMqtt ? "JSON Path" : "Register Address / Tag Name"} are required`);
      return;
    }
    const isDirect = OUTPUT_SIGNAL_TYPES.includes(mapForm.signal_type) && mapForm.write_mode === "direct";
    if (isDirect && (!mapForm.modbus_slave_id || !mapForm.modbus_start_address)) {
      toast.error("Direct Modbus addressing needs both a Slave ID and a relay/coil number");
      return;
    }
    try {
      await api.post(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`, {
        gateway_id: ioDrawer.gateway_id, device_id: deviceIdForSlave(ioDrawer.gateway_id, mapForm.slave_id),
        io_point_name: mapForm.io_point_name,
        json_path: isCustomMqtt ? mapForm.json_path : undefined,
        io_point_address: isCustomMqtt ? undefined : mapForm.io_point_address,
        signal_type: mapForm.signal_type, engineering_unit: mapForm.engineering_unit || undefined,
        engineering_min: numOrUndefined(mapForm.engineering_min),
        engineering_max: numOrUndefined(mapForm.engineering_max),
        readback_io_point_name: mapForm.readback_io_point_name || undefined,
        write_mode: isDirect ? "direct" : "tag",
        modbus_slave_id: isDirect ? mapForm.modbus_slave_id : undefined,
        modbus_start_address: isDirect ? mapForm.modbus_start_address : undefined,
        modbus_function_code: isDirect ? (mapForm.modbus_function_code ?? 15) : undefined,
        modbus_transport: isDirect ? (mapForm.modbus_transport ?? 0) : undefined,
        modbus_tcp_ip: isDirect ? (mapForm.modbus_tcp_ip || "0.0.0.0") : undefined,
        modbus_tcp_port: isDirect ? (mapForm.modbus_tcp_port || 502) : undefined,
      });
      toast.success("Mapping added");
      setMapForm({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "", engineering_min: "", engineering_max: "", readback_io_point_name: "", write_mode: "tag", slave_id: "" });
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
      setIoRows(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add mapping"); }
  };

  return (
    <div className="space-y-6" data-testid="gateways-page">
      {showOnboardBanner && (
        <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3" data-testid="onboard-banner">
          <PartyPopper className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-700 text-sm">Setup complete — your gateway is registered!</div>
            <div className="text-xs text-gray-400 mt-0.5">
              Default IO mappings for your WT-460M have been pre-filled. Power on the device and live data will appear on the dashboard within seconds.
              You can add or edit mappings below.
            </div>
          </div>
          <button onClick={() => { setShowOnboardBanner(false); setSearchParams({}); }} className="text-gray-500 hover:text-gray-900 ml-2 mt-0.5" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → Hardware Gateways</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Hardware Gateways</h1>
          <p className="text-sm text-gray-500 mt-1">Universal Hardware Abstraction Layer · BACnet / Modbus / MQTT / REST · brand-agnostic</p>
        </div>
        <div className="flex gap-2">
          {canManage && <>
          <Button onClick={() => setCsvOpen(true)} variant="outline" className="border-gray-200" data-testid="import-gateways-btn">
            <Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Import CSV
          </Button>
          <Button onClick={() => { setEditing(null); setForm(initialForm()); setStep(1); setOpen(true); }} className="f360-gradient border-0" data-testid="add-gateway-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Gateway
          </Button>
          </>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testid="kpi-total" label="Total Gateways" value={kpis.total} icon={Server} tone="blue" />
        <KpiCard testid="kpi-online" label="Online" value={kpis.online} icon={Wifi} tone="green" />
        <KpiCard testid="kpi-issues" label="Offline / Error" value={kpis.issues} icon={WifiOff} tone={kpis.issues ? "red" : "green"} />
        <KpiCard testid="kpi-io" label="Mapped I/O Points" value={kpis.io} icon={Activity} tone="orange" />
        <KpiCard
          testid="kpi-stuck"
          label="Stuck Registers"
          value={stuckSummary.count}
          icon={AlertCircle}
          tone={stuckSummary.count ? "red" : "green"}
          onClick={() => setStuckPopoverOpen((o) => !o)}
        />
      </div>

      {stuckPopoverOpen && (
        <div className="glass rounded-lg p-4" data-testid="stuck-registers-list">
          {stuckSummary.items.length === 0 ? (
            <div className="text-gray-500 text-sm text-center py-3">No registers have been stuck for over an hour. 🎉</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Stuck Registers (analog, unchanged &gt;1h)</div>
              {stuckSummary.items.map((it, i) => (
                <div
                  key={`${it.gateway_id}-${it.register_name}-${i}`}
                  className="flex items-center justify-between gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                  onClick={() => { setStuckPopoverOpen(false); openLog({ gateway_id: it.gateway_id, gateway_name: it.gateway_name }); }}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{it.gateway_name || it.gateway_id}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{it.register_name}</div>
                  </div>
                  <div className="text-[11px] text-red-700 font-semibold whitespace-nowrap">stuck {fmtAgo(it.seconds_since_change)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="glass rounded-lg p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Search name or IP" className="pl-9 bg-gray-50 border-gray-200" data-testid="gw-search" />
        </div>
        <Select value={filter.site} onValueChange={(v) => setFilter({ ...filter, site: v })}>
          <SelectTrigger className="w-44 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.brand} onValueChange={(v) => setFilter({ ...filter, brand: v })}>
          <SelectTrigger className="w-52 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All brands</SelectItem>{Object.keys(adapters).map((k) => <SelectItem key={k} value={k}>{brandLabel(k)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.protocol} onValueChange={(v) => setFilter({ ...filter, protocol: v })}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All protocols</SelectItem>{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-36 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="connected">Connected</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="disconnected">Disconnected</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-gray-500 ml-2">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
          Show decommissioned
        </label>
      </div>

      {/* Gateway cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((g) => {
          const tone = STATUS_TONE[g.connection_status] || STATUS_TONE.unknown;
          const siteName = sites.find((s) => s.site_id === g.site_id)?.name || g.site_id;
          return (
            <div key={g.gateway_id} className={`glass rounded-lg p-5 hover:border-orange-500/30 transition ${g.is_active === false ? "opacity-60" : ""}`} data-testid={`gw-card-${g.gateway_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${tone.bg} ${tone.ring}`}></span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${tone.text}`}>{tone.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {g.is_active === false && <Badge className="bg-slate-50 text-slate-700 border-slate-200 text-[10px]">DECOMMISSIONED</Badge>}
                  <Badge className="border border-gray-200 bg-gray-50 text-gray-700 text-[10px]">{brandLabel(g.hardware_brand)}</Badge>
                </div>
              </div>
              <h3 className="text-lg font-bold mt-3">{g.gateway_name}</h3>
              <div className="text-xs text-gray-500">{siteName} · {g.hardware_model || "Unspecified model"}</div>
              <div className="text-[11px] text-gray-400 font-mono mt-0.5">IMEI: {g.gateway_id}</div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Protocol</div><div className="font-bold text-orange-600">{PROTOCOL_LABELS[g.primary_protocol]}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">{g.primary_protocol === "mqtt" ? "Broker" : "IP"}</div><div className="font-mono text-[11px]">{g.primary_protocol === "mqtt" ? (g.mqtt_broker_url || "—").replace(/^mqtts?:\/\//, "").slice(0, 24) : (g.ip_address || "—")}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Port</div><div className="font-mono">{g.port}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">I/O Points</div><div className="font-bold">{g.io_mappings_count || 0}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Poll</div><div className="font-bold">{g.poll_interval_seconds}s</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Firmware</div><div className="font-mono text-[11px]">{g.firmware_version || "—"}</div></div>
              </div>

              <div className="text-[11px] text-gray-500 mt-3">Last heartbeat: {g.last_heartbeat ? new Date(g.last_heartbeat).toLocaleString("en-IN") : "never"}</div>
              {g.primary_protocol !== "mqtt" && (
                <div className="flex items-start gap-1.5 mt-2 text-[11px] text-amber-600">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} />
                  No data ingestion support yet for {PROTOCOL_LABELS[g.primary_protocol]} — this gateway will not produce telemetry.
                </div>
              )}

              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" onClick={() => openIo(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`io-btn-${g.gateway_id}`}><Plug className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> IO Mappings</Button>
                <Button size="sm" onClick={() => openLog(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`log-btn-${g.gateway_id}`}><ScrollText className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Connection Log</Button>
                {canManage && <Button size="sm" onClick={() => viewCredentials(g)} className="bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-500/30" data-testid={`cred-btn-${g.gateway_id}`}><KeyRound className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> MQTT Credentials</Button>}
                {canManage && canSimulate(g) && (
                  <Button size="sm" onClick={() => openSimulate(g)} className="bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-500/30" data-testid={`sim-btn-${g.gateway_id}`}><FlaskConical className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Simulate</Button>
                )}
                {canManage && <Button size="sm" onClick={() => openEdit(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`edit-btn-${g.gateway_id}`}><Settings className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Edit</Button>}
                <Button size="sm" onClick={() => test(g)} className="bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-500/30" data-testid={`test-btn-${g.gateway_id}`}><Activity className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Test</Button>
                {!canManage ? null : g.is_active === false ? (
                  <Button size="sm" onClick={() => reactivate(g)} className="bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-500/30" data-testid={`reactivate-btn-${g.gateway_id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Reactivate</Button>
                ) : (
                  <>
                    <Button size="sm" onClick={() => openReplace(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`replace-btn-${g.gateway_id}`}><ArrowLeftRight className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Replace Hardware</Button>
                    <Button size="sm" onClick={() => decommission(g)} className="bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-500/30" data-testid={`decom-btn-${g.gateway_id}`}><Archive className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Decommission</Button>
                  </>
                )}
                {canManage && <Button size="sm" onClick={() => remove(g)} className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-500/30 ml-auto" data-testid={`del-btn-${g.gateway_id}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>}
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="glass rounded-lg p-10 text-center text-gray-500 col-span-full">No gateways match your filters.</div>}
      </div>

      {/* Add/Edit wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing ? "Edit Hardware Gateway" : "Add Hardware Gateway"}
              <span className="text-xs text-gray-500 ml-auto">Step {step} of 3</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex-1 h-1.5 rounded ${step >= n ? "f360-gradient" : "bg-gray-100"}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Gateway Name *</Label><Input value={form.gateway_name} onChange={(e) => setForm({ ...form, gateway_name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-name" /></div>
              {!editing && (
                <div className="col-span-2">
                  <Label>Serial Number / IMEI *</Label>
                  <Input value={form.gateway_id} onChange={(e) => setForm({ ...form, gateway_id: e.target.value.trim() })} className="bg-gray-50 border-gray-200 mt-1 font-mono" placeholder="e.g. 868221079916055" data-testid="gw-serial" />
                  <div className="text-[11px] text-amber-600 mt-1">Must match the device IMEI exactly — this is used to match incoming MQTT messages.</div>
                </div>
              )}
              <div className="col-span-2"><Label>Site *</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Hardware Brand *</Label>
                <Select value={form.hardware_brand} onValueChange={(v) => setForm({ ...form, hardware_brand: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-brand"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.keys(adapters).map((k) => <SelectItem key={k} value={k}>{brandLabel(k)}</SelectItem>)}</SelectContent>
                </Select>
                {adapters[form.hardware_brand]?.notes && <div className="text-[11px] text-amber-300/80 mt-1 italic">{adapters[form.hardware_brand].notes}</div>}
              </div>
              <div><Label>Hardware Model</Label><Input value={form.hardware_model || ""} onChange={(e) => setForm({ ...form, hardware_model: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><Label>Firmware Version</Label><Input value={form.firmware_version || ""} onChange={(e) => setForm({ ...form, firmware_version: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="v3.2.1" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Primary Protocol *</Label>
                <Select value={form.primary_protocol} onValueChange={(v) => setForm({ ...form, primary_protocol: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}{k !== "mqtt" ? " (no data ingestion yet)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Secondary Protocol</Label>
                <Select value={form.secondary_protocol || "_none"} onValueChange={(v) => setForm({ ...form, secondary_protocol: v === "_none" ? "" : v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="_none">None</SelectItem>{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}{k !== "mqtt" ? " (no data ingestion yet)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.primary_protocol !== "mqtt" && (
                <div className="col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    Data ingestion for <strong>{PROTOCOL_LABELS[form.primary_protocol]}</strong> is not yet implemented. Only MQTT
                    devices currently send live data to Fire360Pro — this gateway can be registered and IP/port tested for
                    reachability, but it will not produce telemetry until {PROTOCOL_LABELS[form.primary_protocol]} support is added.
                  </div>
                </div>
              )}
              {(form.primary_protocol === "bacnet_ip" || form.primary_protocol === "modbus_tcp" || form.primary_protocol === "rest_api") && (<>
                <div><Label>IP Address</Label><Input value={form.ip_address || ""} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="192.168.10.5" /></div>
                <div><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </>)}
              {form.primary_protocol === "bacnet_ip" && (
                <div><Label>BACnet Device ID</Label><Input type="number" value={form.bacnet_device_id || ""} onChange={(e) => setForm({ ...form, bacnet_device_id: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              )}
              {(form.primary_protocol === "modbus_tcp" || form.primary_protocol === "modbus_rtu") && (
                <div>
                  <Label>Modbus Unit ID (1–247) *</Label>
                  <Input type="number" min="1" max="247" value={form.modbus_unit_id || 1} onChange={(e) => setForm({ ...form, modbus_unit_id: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" />
                  {form.primary_protocol === "modbus_rtu" && <div className="text-[11px] text-amber-600 mt-1">RTU requires RS-485 wiring — confirm baud rate, parity, and stop bits with the field engineer.</div>}
                </div>
              )}
              {form.primary_protocol === "mqtt" && (<>
                <div className="col-span-2"><Label>MQTT Broker URL *</Label><Input value={form.mqtt_broker_url || ""} onChange={(e) => setForm({ ...form, mqtt_broker_url: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder={DEFAULT_MQTT_BROKER} /></div>
                <div className="col-span-2">
                  <Label>Topic Prefix *</Label>
                  <Input value={form.mqtt_topic_prefix || ""} onChange={(e) => setForm({ ...form, mqtt_topic_prefix: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="iot1/{IMEI}/" />
                </div>
                <div className="col-span-2 text-[11px] text-amber-600 -mt-1">
                  {form.hardware_brand === "custom_mqtt"
                    ? "Configure your device to publish JSON to this exact topic. After saving, define field mappings in the IO Mappings drawer below — no code required."
                    : <>The device must be configured with this exact broker URL and a publish topic of <span className="font-mono">{`iot1/${form.gateway_id || "{IMEI}"}/event/`}</span> on its own config tool.</>}
                </div>
              </>)}
              {form.primary_protocol === "rest_api" && (
                <div className="col-span-2"><Label>API Endpoint</Label><Input value={form.api_endpoint || ""} onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="https://api.gateway.local/v1" /></div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Poll Interval (sec)</Label><Input type="number" min="5" value={form.poll_interval_seconds} onChange={(e) => setForm({ ...form, poll_interval_seconds: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div className="col-span-2"><Label>Notes</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="Field engineer notes, panel positioning, cable runs..." /></div>
              <div className="col-span-2 bg-gray-50 rounded-lg p-4 text-xs space-y-1.5">
                <div className="text-orange-700 font-bold uppercase tracking-wider text-[10px]">Review</div>
                <div><span className="text-gray-500">Name:</span> {form.gateway_name}</div>
                {!editing && <div><span className="text-gray-500">Serial / IMEI:</span> <span className="font-mono">{form.gateway_id || "—"}</span></div>}
                {editing && editing.site_id !== form.site_id ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800" data-testid="gw-review-site-move">
                    <span className="font-bold">Moving site:</span> {siteLabel(editing.site_id)} → <span className="font-bold">{siteLabel(form.site_id)}</span>
                  </div>
                ) : (
                  <div className="text-sm"><span className="text-gray-500 text-xs">Site:</span> <span className="font-bold text-gray-900" data-testid="gw-review-site">{siteLabel(form.site_id)}</span></div>
                )}
                <div><span className="text-gray-500">Brand / Model:</span> {brandLabel(form.hardware_brand)} · {form.hardware_model}</div>
                <div><span className="text-gray-500">Protocol:</span> {PROTOCOL_LABELS[form.primary_protocol]} {form.secondary_protocol ? `+ ${PROTOCOL_LABELS[form.secondary_protocol]}` : ""}</div>
                <div><span className="text-gray-500">Endpoint:</span> {form.primary_protocol === "mqtt" ? form.mqtt_broker_url : `${form.ip_address || "—"}:${form.port}`}</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <div className="flex gap-2 w-full justify-between">
              <Button variant="ghost" onClick={() => step === 1 ? setOpen(false) : setStep(step - 1)}>
                {step === 1 ? "Cancel" : <><ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} /> Back</>}
              </Button>
              {step < 3 ? (
                <Button onClick={goNext} className="f360-gradient border-0" data-testid="gw-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
              ) : (
                <Button onClick={save} className="f360-gradient border-0" data-testid="gw-save-btn"><Check className="w-4 h-4 mr-1" strokeWidth={2.5} /> {editing ? "Update Gateway" : "Create Gateway"}</Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg" data-testid="gw-delete-dialog">
          {deleteTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" strokeWidth={2.5} /> Permanently delete this gateway?
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-lg bg-gray-50 p-3 text-sm space-y-1">
                <div><span className="text-gray-500">Name:</span> <span className="font-bold">{deleteTarget.gateway_name}</span></div>
                <div><span className="text-gray-500">IMEI:</span> <span className="font-mono">{deleteTarget.gateway_id}</span></div>
                <div><span className="text-gray-500">Site:</span> <span className="font-bold">{siteLabel(deleteTarget.site_id)}</span></div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <div className="font-bold mb-1">This cannot be undone. It will:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Remove the gateway from this site</li>
                  <li>Delete its {deleteTarget.io_mappings_count ?? deleteTarget.total_io_points ?? 0} IO mapping{(deleteTarget.io_mappings_count ?? deleteTarget.total_io_points) === 1 ? "" : "s"}</li>
                  <li>Delete its MQTT login — the device is disconnected and cannot reconnect</li>
                </ul>
              </div>
              {deleteTarget.is_active !== false && (
                <div className="text-sm text-gray-600">
                  Taking it out of service or moving it?{" "}
                  <button type="button" className="font-bold text-amber-700 underline" onClick={() => { const g = deleteTarget; setDeleteTarget(null); decommission(g); }} data-testid="gw-delete-decommission-instead">
                    Decommission instead
                  </button>{" "}
                  — keeps its mappings and history, and can be reactivated.
                </div>
              )}
              <div>
                <Label>Type the IMEI <span className="font-mono font-bold">{deleteTarget.gateway_id}</span> to confirm</Label>
                <Input value={deleteTyped} onChange={(e) => setDeleteTyped(e.target.value)} className="bg-gray-50 border-gray-200 mt-1 font-mono" autoComplete="off" data-testid="gw-delete-confirm-input" />
              </div>
              <DialogFooter>
                <div className="flex gap-2 w-full justify-end">
                  <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                  <Button onClick={confirmRemove} disabled={deleting || deleteTyped.trim() !== deleteTarget.gateway_id} className="bg-red-600 hover:bg-red-700 text-white" data-testid="gw-delete-confirm-btn">
                    <Trash2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> {deleting ? "Deleting…" : "Delete permanently"}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* IO Mappings drawer */}
      <Sheet open={!!ioDrawer} onOpenChange={(o) => !o && setIoDrawer(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-2xl overflow-y-auto" data-testid="io-drawer">
          {ioDrawer && (
            <>
              <SheetHeader>
                <SheetTitle className="text-gray-900 text-xl">IO Mappings — {ioDrawer.gateway_name}</SheetTitle>
                <div className="text-xs text-gray-500">Maps hardware I/O points to Fire360Pro devices · {PROTOCOL_LABELS[ioDrawer.primary_protocol]}</div>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {ioRows.map((m) => (
                  editingMappingId === m.mapping_id ? (
                    <div key={m.mapping_id} className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid={`io-row-edit-${m.mapping_id}`}>
                      {(m.json_path === null || m.json_path === undefined) && (
                        <LiveRegisterFields form={editMapForm} setForm={setEditMapForm} live={liveRegs} slaveOptions={slaveOptions} fieldBg="bg-white" />
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="IO Point Name" value={editMapForm.io_point_name || ""} onChange={(e) => setEditMapForm({ ...editMapForm, io_point_name: e.target.value })} className="bg-white border-gray-200" />
                        {m.json_path !== null && m.json_path !== undefined && (
                          <div>
                            <Input placeholder="JSON Path" value={editMapForm.json_path || ""} onChange={(e) => setEditMapForm({ ...editMapForm, json_path: e.target.value })} className="bg-white border-gray-200 font-mono text-xs" />
                            <JsonPathFeedback parsedSample={parsedSample} path={editMapForm.json_path} />
                          </div>
                        )}
                        <Select value={editMapForm.signal_type} onValueChange={(v) => setEditMapForm({ ...editMapForm, signal_type: v })}>
                          <SelectTrigger className="bg-white border-gray-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 text-gray-900">
                            <SelectItem value="analog_input">Analog Input</SelectItem>
                            <SelectItem value="digital_input">Digital Input</SelectItem>
                            <SelectItem value="binary_input">Binary Input</SelectItem>
                            <SelectItem value="pulse_counter">Pulse Counter</SelectItem>
                            <SelectItem value="digital_output">Digital Output (writable)</SelectItem>
                            <SelectItem value="binary_output">Binary Output (writable)</SelectItem>
                            <SelectItem value="analog_output">Analog Output (writable)</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input placeholder="Unit (e.g. bar, °C)" value={editMapForm.engineering_unit || ""} onChange={(e) => setEditMapForm({ ...editMapForm, engineering_unit: e.target.value })} className="bg-white border-gray-200" />
                        {["analog_input", "analog_output"].includes(editMapForm.signal_type) && (<>
                          <Input type="number" step="any" placeholder={`Min value (e.g. 0.0 ${editMapForm.engineering_unit || "bar"})`} value={editMapForm.engineering_min ?? ""} onChange={(e) => setEditMapForm({ ...editMapForm, engineering_min: e.target.value === "" ? undefined : e.target.value })} className="bg-white border-gray-200" />
                          <Input type="number" step="any" placeholder={`Max value (e.g. 10.0 ${editMapForm.engineering_unit || "bar"})`} value={editMapForm.engineering_max ?? ""} onChange={(e) => setEditMapForm({ ...editMapForm, engineering_max: e.target.value === "" ? undefined : e.target.value })} className="bg-white border-gray-200" />
                        </>)}
                      </div>
                      {OUTPUT_SIGNAL_TYPES.includes(editMapForm.signal_type) && (
                        <>
                          <WriteAddressingFields form={editMapForm} setForm={setEditMapForm} fieldBg="bg-white" />
                          {m.json_path !== null && m.json_path !== undefined && (
                            <Input
                              placeholder="Readback parameter (optional)"
                              value={editMapForm.readback_io_point_name || ""}
                              onChange={(e) => setEditMapForm({ ...editMapForm, readback_io_point_name: e.target.value })}
                              className="bg-white border-gray-200 font-mono text-xs"
                            />
                          )}
                        </>
                      )}
                      <div className="flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={cancelEditMapping} className="text-gray-500 h-7"><X className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Cancel</Button>
                        <Button size="sm" onClick={() => updateMapping(m.mapping_id)} className="f360-gradient border-0 h-7"><Check className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div key={m.mapping_id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-testid={`io-row-${m.mapping_id}`}>
                      <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{m.io_point_name || m.io_point_address}</div>
                        <div className="text-[11px] text-gray-500">
                          {m.json_path ? <>path <span className="font-mono text-orange-700">{m.json_path}</span></> : <>addr <span className="font-mono text-orange-700">{m.io_point_address}</span></>}
                          {" "}· {m.signal_type} · {m.engineering_min}–{m.engineering_max} {m.engineering_unit}
                          {m.readback_io_point_name && <> · reads back as <span className="font-mono text-orange-700">{m.readback_io_point_name}</span></>}
                          {m.write_mode === "direct" && (
                            <> · writes to <span className="font-mono text-orange-700">slave {m.modbus_slave_id} coil {m.modbus_start_address}</span> (FC{m.modbus_function_code ?? 15})</>
                          )}
                          {(m.json_path === null || m.json_path === undefined) && (
                            slaveIdFromDeviceId(m.gateway_id, m.device_id)
                              ? <> · <span className="font-mono text-orange-700">slave {slaveIdFromDeviceId(m.gateway_id, m.device_id)}</span></>
                              : <> · <span className="text-amber-500">unscoped device_id — confirmation may match the wrong slave</span></>
                          )}
                        </div>
                      </div>
                      {canManage && <><Button size="sm" variant="ghost" onClick={() => startEditMapping(m)} className="text-gray-500 h-7" data-testid={`edit-io-${m.mapping_id}`}><Settings className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteIo(m)} className="text-red-400 h-7"><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button></>}
                    </div>
                  )
                ))}
                {!ioRows.length && <div className="text-gray-500 text-sm text-center py-6">No IO mappings yet. Add via the device page or upload a CSV.</div>}
              </div>

              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3">
                <div className="text-xs uppercase tracking-widest text-orange-700 font-bold">Registered Slave IDs</div>
                <div className="text-[11px] text-gray-500">
                  Declares which Modbus Slave IDs are valid under this gateway. Once at least one
                  is registered, data from any other Slave ID is dropped and logged instead of stored.
                </div>
                <div className="space-y-2">
                  {slaveRows.map((s) => (
                    <div key={s.slave_id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-testid={`slave-row-${s.slave_id}`}>
                      <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate flex items-center gap-2">
                          Slave {s.slave_id}{s.label ? ` — ${s.label}` : ""}
                          {liveRegs.some((r) => r.slave === String(s.slave_id))
                            ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">live</span>
                            : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-500">not reporting</span>}
                        </div>
                        <div className="text-[11px] text-gray-500">Registered {s.registered_at ? new Date(s.registered_at).toLocaleString("en-IN") : "—"}</div>
                      </div>
                      {canManage && <Button size="sm" variant="ghost" onClick={() => deleteSlave(s.slave_id)} className="text-red-400 h-7"><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>}
                    </div>
                  ))}
                  {!slaveRows.length && <div className="text-gray-500 text-sm text-center py-3">No slave IDs registered yet — all incoming slave IDs are currently accepted.</div>}
                </div>
                {canManage && <>
                <div className="grid grid-cols-2 gap-2">
                  <SlavePicker value={slaveForm.slave_id} allowNone={false} fieldBg="bg-gray-50" testid="register-slave"
                    options={slaveOptions.filter((o) => !slaveRows.some((s) => String(s.slave_id) === o.id))}
                    onChange={(v) => setSlaveForm({ ...slaveForm, slave_id: v })} />
                  <Input placeholder="Label (e.g. Fire Panel)" value={slaveForm.label} onChange={(e) => setSlaveForm({ ...slaveForm, label: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <Button onClick={addSlave} className="f360-gradient border-0" size="sm"><Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Register Slave ID</Button>
                </>}
              </div>

              {ioDrawer.hardware_brand !== "custom_mqtt" && (
              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3" data-testid="live-registers-section">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-orange-700 font-bold">Live Registers</div>
                    <div className="text-[11px] text-gray-500">What this gateway is reporting now — same data as Gateway Commands' Live Register Monitor.</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => loadLive()} disabled={liveLoading} className="h-7 text-xs text-gray-600">
                      <RotateCcw className={`w-3.5 h-3.5 mr-1 ${liveLoading ? "animate-spin" : ""}`} strokeWidth={2.5} />Refresh
                    </Button>
                    {canManage && liveRegs.some((r) => !mappingFor(r)) && (
                      <Button size="sm" onClick={mapAllLive} disabled={mappingAll} className="f360-gradient border-0 h-7 text-xs" data-testid="map-all-live">
                        <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />{mappingAll ? "Adding…" : `Map all unmapped (${liveRegs.filter((r) => !mappingFor(r)).length})`}
                      </Button>
                    )}
                  </div>
                </div>
                {liveRegs.length > 0 && slaveOptions.length > 1 && (
                  <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Filter by slave">
                    {[["all", "All", liveRegs.length], ...[...new Set(liveRegs.map((r) => r.slave))].map((sl) => [sl, `Slave ${sl}`, liveRegs.filter((r) => r.slave === sl).length])].map(([v, label, n]) => (
                      <button key={v} type="button" aria-pressed={liveSlaveFilter === v} onClick={() => setLiveSlaveFilter(v)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${liveSlaveFilter === v ? "border-orange-400 bg-orange-50 text-orange-700" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"}`}>
                        {label}<span className="ml-1 font-normal opacity-70">{n}</span>
                      </button>
                    ))}
                  </div>
                )}
                {liveRegs.length ? (
                  <div className="rounded-lg border border-gray-200 overflow-auto max-h-[360px]">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 sticky top-0">
                        <tr><th className="text-left px-3 py-2">Slave</th><th className="text-left px-3 py-2">Register</th><th className="text-right px-3 py-2">Value</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Mapping</th><th className="px-3 py-2" /></tr>
                      </thead>
                      <tbody>
                        {liveRegs.filter((r) => liveSlaveFilter === "all" || r.slave === liveSlaveFilter).map((r) => {
                          const m = mappingFor(r);
                          const g = guessSignalType(r.parameter);
                          return (
                            <tr key={`${r.slave}::${r.parameter}`} className="border-t border-gray-100" data-testid={`live-reg-${r.slave}-${r.parameter}`}>
                              <td className="px-3 py-1.5 font-mono text-gray-600">{r.slave || "—"}</td>
                              <td className="px-3 py-1.5 font-mono font-semibold text-gray-900">{r.parameter}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-gray-700 tabular-nums">{fmtLiveValue(r.value)}{r.unit ? ` ${r.unit}` : ""}</td>
                              <td className={`px-3 py-1.5 text-[11px] ${OUTPUT_SIGNAL_TYPES.includes(g) ? "text-amber-700" : "text-gray-500"}`}>{LIVE_TYPE_LABEL[g] || g}</td>
                              <td className="px-3 py-1.5">
                                {m ? <span className="text-emerald-700">✓ {m.io_point_name || m.io_point_address}</span> : <span className="text-gray-400">Not mapped</span>}
                              </td>
                              <td className="px-3 py-1.5 text-right">
                                {canManage && !m && <button onClick={() => pickLiveRegister(r)} className="text-orange-700 font-semibold hover:underline">Use</button>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm text-center py-3 bg-gray-50 rounded-lg">
                    {liveLoading ? "Loading live data…" : "No live data from this gateway yet — registers appear here once it reports."}
                  </div>
                )}
              </div>
              )}

              {canManage && (
              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3" data-testid="add-mapping-section">
                <div className="text-xs uppercase tracking-widest text-orange-700 font-bold">Add Mapping</div>
                {ioDrawer.hardware_brand !== "custom_mqtt" && (
                  <LiveRegisterFields form={mapForm} setForm={setMapForm} live={liveRegs} slaveOptions={slaveOptions} fieldBg="bg-gray-50" />
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="IO Point Name (e.g. Pump Reset Relay)" value={mapForm.io_point_name} onChange={(e) => setMapForm({ ...mapForm, io_point_name: e.target.value })} className="bg-gray-50 border-gray-200" />
                  {ioDrawer.hardware_brand === "custom_mqtt" && (
                    <div>
                      <Input placeholder="JSON Path (e.g. data.temp1)" value={mapForm.json_path} onChange={(e) => setMapForm({ ...mapForm, json_path: e.target.value })} className="bg-gray-50 border-gray-200 font-mono text-xs" />
                      <JsonPathFeedback parsedSample={parsedSample} path={mapForm.json_path} />
                    </div>
                  )}
                  <Select value={mapForm.signal_type} onValueChange={(v) => setMapForm({ ...mapForm, signal_type: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="analog_input">Analog Input</SelectItem>
                      <SelectItem value="digital_input">Digital Input</SelectItem>
                      <SelectItem value="binary_input">Binary Input</SelectItem>
                      <SelectItem value="pulse_counter">Pulse Counter</SelectItem>
                      <SelectItem value="digital_output">Digital Output (writable)</SelectItem>
                      <SelectItem value="binary_output">Binary Output (writable)</SelectItem>
                      <SelectItem value="analog_output">Analog Output (writable)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Unit (e.g. bar, °C)" value={mapForm.engineering_unit} onChange={(e) => setMapForm({ ...mapForm, engineering_unit: e.target.value })} className="bg-gray-50 border-gray-200" />
                  {["analog_input", "analog_output"].includes(mapForm.signal_type) && (<>
                    <Input type="number" step="any" placeholder={`Min value (e.g. 0.0 ${mapForm.engineering_unit || "bar"})`} value={mapForm.engineering_min} onChange={(e) => setMapForm({ ...mapForm, engineering_min: e.target.value })} className="bg-gray-50 border-gray-200" />
                    <Input type="number" step="any" placeholder={`Max value (e.g. 10.0 ${mapForm.engineering_unit || "bar"})`} value={mapForm.engineering_max} onChange={(e) => setMapForm({ ...mapForm, engineering_max: e.target.value })} className="bg-gray-50 border-gray-200" />
                  </>)}
                </div>
                {OUTPUT_SIGNAL_TYPES.includes(mapForm.signal_type) && (
                  <>
                    <div className="text-[11px] text-amber-600">Writable registers appear as remote controls on the Gateway Commands page immediately after saving.</div>
                    <WriteAddressingFields form={mapForm} setForm={setMapForm} fieldBg="bg-gray-50" />
                    {ioDrawer.hardware_brand === "custom_mqtt" && (
                      <Input
                        placeholder="Readback parameter (optional — only if the device reports this output back under a different name)"
                        value={mapForm.readback_io_point_name}
                        onChange={(e) => setMapForm({ ...mapForm, readback_io_point_name: e.target.value })}
                        className="bg-gray-50 border-gray-200 font-mono text-xs"
                      />
                    )}
                  </>
                )}
                <Button onClick={addMapping} className="f360-gradient border-0" size="sm"><Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Add Mapping</Button>

                {ioDrawer.hardware_brand === "custom_mqtt" && (
                  <>
                    <div className="text-[11px] text-gray-500 mt-2">Sample payload received from this device — use it to find the right JSON path:</div>
                    {sample?.last_raw_sample ? (
                      <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{sample.last_raw_sample}</pre>
                    ) : (
                      <div className="text-gray-500 text-xs">No payload received yet — power on the device and publish to <span className="font-mono">{`fire360pro/custom/${ioDrawer.gateway_id}/data`}</span>, then reopen this drawer.</div>
                    )}
                  </>
                )}
              </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Connection Log drawer */}
      <Sheet open={!!logDrawer} onOpenChange={(o) => !o && setLogDrawer(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-2xl overflow-y-auto" data-testid="log-drawer">
          {logDrawer && (
            <>
              <SheetHeader>
                <SheetTitle className="text-gray-900 text-xl">Connection Log — {logDrawer.gateway_name}</SheetTitle>
                <div className="text-xs text-gray-500">Real connect/disconnect/error events and recent telemetry — self-service diagnostics, no SSH required</div>
              </SheetHeader>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold mb-2">Event Timeline</div>
                <div className="space-y-1.5">
                  {logData.events.map((ev) => {
                    const tone = EVENT_TONE[ev.event_type] || EVENT_TONE.disconnected;
                    return (
                      <div key={ev.log_id} className="flex items-start gap-2 bg-gray-50 rounded p-2.5 text-xs" data-testid={`log-event-${ev.log_id}`}>
                        <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${tone.dot}`}></span>
                        <div className="flex-1 min-w-0">
                          <span className={`font-bold uppercase tracking-wider text-[10px] ${tone.text}`}>{ev.event_type.replace("_", " ")}</span>
                          <span className="text-gray-500 ml-2">{new Date(ev.created_at).toLocaleString("en-IN")}</span>
                          {ev.detail && <div className="text-gray-600 mt-0.5">{ev.detail}</div>}
                        </div>
                      </div>
                    );
                  })}
                  {!logData.events.length && <div className="text-gray-500 text-sm text-center py-4">No connection events recorded yet.</div>}
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold mb-2">Recent Messages</div>
                <div className="space-y-1.5">
                  {logData.recent_messages.map((m, i) => {
                    const key = `${m.ingested_at}_${i}`;
                    const open = expandedMsg === key;
                    return (
                      <div key={key} className="bg-gray-50 rounded text-xs" data-testid={`log-msg-${i}`}>
                        <button className="w-full flex items-center gap-2 p-2.5 text-left" onClick={() => setExpandedMsg(open ? null : key)}>
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
                          <span className="text-gray-500">{new Date(m.ingested_at).toLocaleString("en-IN")}</span>
                          <span className="text-orange-700 font-mono">slave {m.slave_id}</span>
                          <span className="text-gray-500 ml-auto">{Object.keys(m.registers || {}).length} registers</span>
                        </button>
                        {open && (
                          <pre className="px-2.5 pb-2.5 font-mono text-[11px] text-gray-700 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(m.registers, null, 2)}</pre>
                        )}
                      </div>
                    );
                  })}
                  {!logData.recent_messages.length && <div className="text-gray-500 text-sm text-center py-4">No messages received yet from this gateway.</div>}
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold mb-2">Register Health</div>
                <div className="text-[11px] text-gray-500 mb-2">Analog registers that haven't changed value in over an hour are flagged — digital/binary registers staying constant is normal and never flagged.</div>
                <div className="space-y-1.5">
                  {logData.register_health.map((r) => (
                    <div key={r.register_name} className="flex items-center justify-between gap-2 bg-gray-50 rounded p-2.5 text-xs" data-testid={`reg-health-${r.register_name}`}>
                      <span className="font-mono text-gray-700">{r.register_name}</span>
                      <span className="text-gray-500">value: {r.last_value}</span>
                      <span className="text-gray-500">changed {fmtAgo(r.seconds_since_change)} ago</span>
                      {r.stuck && <Badge className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px]">Possibly Stuck</Badge>}
                    </div>
                  ))}
                  {!logData.register_health.length && <div className="text-gray-500 text-sm text-center py-4">No register history yet.</div>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Replace Hardware dialog */}
      <Dialog open={!!replaceDrawer} onOpenChange={(o) => !o && setReplaceDrawer(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader><DialogTitle>Replace Hardware — {replaceDrawer?.gateway_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              Use this when the physical device is being swapped (e.g. failed hardware). All IO
              mappings carry over automatically to the new device. The old gateway record is kept
              for historical telemetry but marked decommissioned.
            </p>
            <div>
              <Label>New Device Serial Number / IMEI *</Label>
              <Input value={newSerial} onChange={(e) => setNewSerial(e.target.value.trim())} className="bg-gray-50 border-gray-200 mt-1 font-mono" placeholder="e.g. 868221079916099" data-testid="replace-serial-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceDrawer(null)} className="border-gray-200">Cancel</Button>
            <Button onClick={sendReplace} disabled={replacing} className="f360-gradient border-0" data-testid="replace-confirm-btn">
              {replacing ? "Replacing…" : "Replace Hardware"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Gateways from CSV"
        endpoint="/hardware/gateways/bulk-import"
        templateRows={GATEWAY_CSV_TEMPLATE}
        templateFilename="gateways_template.csv"
        onDone={load}
      />

      {/* Simulate payload dialog */}
      <Dialog open={!!simDrawer} onOpenChange={(o) => !o && setSimDrawer(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Send Test Payload — {simDrawer?.gateway_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              Publishes a message through the real MQTT pipeline — exactly what a physical device would
              trigger. Leave blank to auto-generate from this gateway's configured IO mappings, or paste
              a custom JSON payload to test an exact shape before connecting hardware.
            </p>
            <textarea
              value={simPayloadText}
              onChange={(e) => setSimPayloadText(e.target.value)}
              placeholder={'Leave blank to auto-generate, or paste custom JSON e.g. {"data": {"temp1": 23.5}}'}
              className="w-full h-28 bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-xs"
              data-testid="sim-payload-input"
            />
            <Button onClick={sendSimulation} disabled={simSending} className="f360-gradient border-0" size="sm" data-testid="sim-send-btn">
              {simSending ? "Sending…" : <><FlaskConical className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Send Test Payload</>}
            </Button>

            {simResult && (
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <div className={`text-xs font-bold uppercase tracking-wider ${simResult.success ? "text-emerald-600" : "text-red-600"}`}>
                  {simResult.success ? "Ingested successfully" : "Not ingested"}
                </div>
                {!simResult.success && (
                  <div className="text-xs text-gray-600">{simResult.message || simResult.error}</div>
                )}
                {simResult.success && (
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(simResult.registers, null, 2)}</pre>
                )}
                {simResult.payload_sent && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-2">Payload Sent</div>
                    <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(simResult.payload_sent, null, 2)}</pre>
                  </>
                )}
                {!simResult.success && (
                  <div className="text-[11px] text-gray-500">See the Connection Log drawer for full event history.</div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* MQTT Credentials dialog */}
      <Dialog open={!!credResult} onOpenChange={(o) => !o && setCredResult(null)}>
        <DialogContent className="max-w-md" data-testid="cred-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-500" /> MQTT Credentials — {credResult?.gateway_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {credResult?.rotated && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                New password generated — the old one has stopped working. Update the device now.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">MQTT Username</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 rounded px-3 py-2 text-sm font-mono">{credResult?.mqtt_username}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(credResult?.mqtt_username || ""); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">MQTT Password</p>
                {credResult?.mqtt_password ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 rounded px-3 py-2 text-sm font-mono break-all" data-testid="cred-password">{showCredPassword ? credResult.mqtt_password : "••••••••••••••••••••"}</code>
                    <Button size="sm" variant="outline" onClick={() => setShowCredPassword((v) => !v)}>{showCredPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</Button>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(credResult.mqtt_password); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="cred-password-missing">
                    <p className="text-xs text-gray-600">Not stored — this gateway was registered before passwords were saved. If you know the password the device uses, save it here; otherwise generate a new one below.</p>
                    <div className="flex items-center gap-2">
                      <Input value={knownPassword} onChange={(e) => setKnownPassword(e.target.value)} placeholder="Password the device already uses" className="bg-gray-50 border-gray-200 font-mono text-sm" autoComplete="off" data-testid="cred-known-input" />
                      <Button size="sm" onClick={saveKnownPassword} disabled={knownPassword.trim().length < 8} className="f360-gradient border-0" data-testid="cred-known-save">Save</Button>
                    </div>
                  </div>
                )}
                {credResult?.password_updated_at && !credResult?.rotated && (
                  <p className="text-[11px] text-gray-400 mt-1">Last set {new Date(credResult.password_updated_at).toLocaleString("en-IN")}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Broker</p>
                <div className="bg-gray-100 rounded px-3 py-2 text-sm space-y-0.5">
                  <div><span className="font-mono">fireacms.com</span> port <span className="font-mono font-bold">8883</span> — TLS on</div>
                  <div className="text-gray-500"><span className="font-mono">fireacms.com</span> port <span className="font-mono font-bold">8884</span> — TLS off (legacy)</div>
                </div>
              </div>
              {credResult?.mqtt_topic_prefix && (
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Topic prefix</p>
                  <code className="block bg-gray-100 rounded px-3 py-2 text-sm font-mono">{credResult.mqtt_topic_prefix.replace("{IMEI}", credResult.gateway_id)}</code>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <div className="flex gap-2 w-full justify-between">
              <Button variant="ghost" onClick={() => rotateCredentials(credResult)} className="text-amber-700" data-testid="cred-rotate-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Generate new password</Button>
              <Button onClick={() => setCredResult(null)} className="f360-gradient border-0">Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
